import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CustomDomainStyles from "screens/Settings/Domains/Domains.styles";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import {
  domainNameValidation,
  getAddDomainPayload,
  getInitialDomainState,
} from "screens/Settings/Domains/DomainsHelpers";
import DomainList from "screens/Settings/Domains/DomainsList/DomainList";
import DomainDetails from "screens/Settings/Domains/DomainDetails/DomainDetails";
import AddDomainButtonIcon from "assets/icons1/AddDomainButtonIcon.svg";
import strings from "global/constants/StringConstants";
import { useTitle } from "utils/UseTitle";
import verifyStatus from "assets/icons1/verifyStatus.svg";
import {
  addCustomDomain,
  getCustomDomains,
  getDomainDetails,
  sendVerificationQNA,
  uploadEmailSampleDoc,
} from "screens/Settings/Domains/Domains.service";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import title from "global/constants/TitleConstants";
import CustomInput from "global/components/CustomInput/CustomInput";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CustomTextarea from "global/components/CustomTextarea/CustomTextarea";
import { useAppSelector } from "utils/hooks";
import { selectEmail } from "redux/authSlice";
import DropZoneIcon from "../../../assets/icons1/dropzone_icon.svg";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import viewpaths from "global/constants/ViewPathConstants";
import { RouterLink } from "components/router-link";
import { KeyboardBackspace } from "@mui/icons-material";

const Domains = () => {
  useTitle(title.DOMAINS);
  const classes = CustomDomainStyles;
  const theme = useTheme();

  const qnaMap: any = {
    "How many total subscribers are on your list?": {
      value: "",
      error: "",
      placeholder: "Ex. 1000",
      type: "number",
      component: "textInput",
    },
    "What is the process of email collection?": {
      value: "",
      error: "",
      placeholder: "Specify the source of email list.",
      type: "text",
      component: "textInput",
    },
    "What kind of emails are you planning to send?": {
      value: "",
      error: "",
      placeholder: "Select type of emails",
      type: "text",
      component: "dropdown",
      options: ["Marketing", "Newsletter", "Promotions", "Other"],
    },
  };

  const initialSampleEmailUrl = {
    value: "",
    error: "",
    fileName: "",
  };

  const initialKeyValue = {
    value: "",
    error: "",
  };

  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [isOpenStartVerification, setIsOpenStartVerification] =
    useState<boolean>(false);

  const [verificationQNA, setVerificationQNA] = useState<any>(qnaMap);
  const [sampleEmailUrl, setSampleEmailUrl] = useState<any>(
    initialSampleEmailUrl
  );
  const [websiteDescription, setWebsiteDescription] =
    useState<any>(initialKeyValue);
  const loggedInUserEmail: string = useAppSelector(selectEmail);

  const [domainToBeAdded, setDomainToBeAdded] = useState<any>(
    getInitialDomainState()
  );
  const [domainToBeDisplayed, setDomainToBeDisplayed] = useState<string>("");
  const [domainStatus, setDomainStatus] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string>(strings.domainsList);
  const [domains, setDomains] = useState<any[]>([]);
  const [domainDetails, setDomainDetails] = useState<any>({});
  const [isStartVerification, setIsStartVerification] =
    useState<boolean>(false);

  useEffect(() => {
    if (currentPage === strings.domainsList) {
      fetchCustomDomains();
    }
  }, [currentPage]);

  useEffect(() => {
    isTruthy(domainToBeDisplayed) && fetchDomainStatus();
  }, []);

  const fetchDomainStatus = async () => {
    try {
      setIsLoading(true);
      const response = await getDomainDetails(domainToBeDisplayed);
      // const formattedRows = getFormattedDomainDetails(response);
      // setDomainDetails(formattedRows);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getColors = {
    CNAMEPending: "#FF9436",
    VerificationPending: "#FF9436",
    Active: "#B2E1CA",
  } as { [key: string]: string };

  const fetchCustomDomains = async () => {
    try {
      setIsLoading(true);
      const response = await getCustomDomains();
      setDomains(response);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validate = (): boolean => {
    let isValid = true;
    if (!isTruthy(websiteDescription.value)) {
      setWebsiteDescription({
        ...websiteDescription,
        error: "Please write description about your website.",
      });

      isValid = false;
    }

    let updatedVerificationQNA = { ...verificationQNA };

    Object.keys(verificationQNA).forEach((question: string) => {
      if (
        (verificationQNA[question].type === "text" &&
          !isTruthy(verificationQNA[question].value)) ||
        (verificationQNA[question].type === "number" &&
          verificationQNA[question].value < 1)
      ) {
        updatedVerificationQNA = {
          ...updatedVerificationQNA,
          [question]: {
            ...updatedVerificationQNA[question],
            error: "Please write the answer.",
          },
        };

        isValid = false;
      }
    });
    setVerificationQNA(updatedVerificationQNA);

    if (!isTruthy(sampleEmailUrl.value)) {
      setSampleEmailUrl({
        ...sampleEmailUrl,
        error: "Please upload the sample of email.",
      });
      isValid = false;
    }
    return isValid;
  };

  const handleSubmitForm = async () => {
    if (validate()) {
      // Extract the `value` properties from verificationQNA
      const extractedValues: any = Object.keys(verificationQNA).reduce(
        (acc: any, question: string) => {
          acc[question] = verificationQNA[question]?.value;
          return acc;
        },
        {}
      );
      const questionAnswerObj = {
        ...extractedValues,
        description: websiteDescription.value,
        attachment: sampleEmailUrl.value,
      };
      await sendVerificationDetails(questionAnswerObj);
      setIsOpenStartVerification(false);
      setWebsiteDescription(initialKeyValue);
      setVerificationQNA(qnaMap);
      setSampleEmailUrl(initialSampleEmailUrl);
    }
  };

  const sendVerificationDetails = async (questionAnswerObj: any) => {
    try {
      setIsLoading(true);
      const response = await sendVerificationQNA(
        questionAnswerObj,
        domainToBeDisplayed
      );
      openSuccessNotification(response.message);
      setIsLoading(false);
      domainViewButtonHandler(
        domainToBeDisplayed,
        strings.EmailDomainStatus.VerificationPending
      );
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const addDomainButtonHandler = () => {
    setIsAddDialogOpen(true);
  };

  const isDomainNameValid = () => {
    const { isValid, errorLadenState } = domainNameValidation(domainToBeAdded);
    const isDuplicate = domains.some(
      (domain) => domain.name === domainToBeAdded.domainName.value
    );

    if (isDuplicate) {
      openErrorNotification("Domain already exists!");
      return false;
    }
    setDomainToBeAdded({ ...errorLadenState });
    return isValid;
  };

  const newDomainAddHandler = async () => {
    try {
      if (isDomainNameValid()) {
        setIsLoading(true);
        const payload = getAddDomainPayload(domainToBeAdded);
        const response = await addCustomDomain(payload);
        openSuccessNotification(
          "Your domain registration is requested successfully."
        );
        setDomainToBeAdded({ ...getInitialDomainState() });
        fetchCustomDomains();
        setIsAddDialogOpen(false);
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeHandler = (event: any) => {
    setDomainToBeAdded({
      ...domainToBeAdded,
      [event.target.name]: { value: event.target.value, error: "" },
    });
  };

  const isDomainDetailsPage = () => {
    return currentPage === strings.domainDetails;
  };

  const domainViewButtonHandler = (
    domainName: string,
    domainStatus: string
  ) => {
    setDomainToBeDisplayed(domainName);
    setDomainStatus(domainStatus);
    setCurrentPage(strings.domainDetails);
  };

  const getAddDomainButton = () => {
    return (
      <Button
        id="domains_add_button"
        variant="contained"
        size="small"
        startIcon={
          <Stack>
            <img src={AddDomainButtonIcon} />
          </Stack>
        }
        onClick={addDomainButtonHandler}
        disabled={isLoading}
      >
        Add Domain
      </Button>
    );
  };

  const addDialogCloseHandler = () => {
    setIsAddDialogOpen(false);
    setDomainToBeAdded({ ...getInitialDomainState() });
  };

  const getDomainNameForAdornment = (domainName: string) => {
    return domainName.length > 35
      ? `@${domainName.substring(0, 35)}...`
      : `@${domainName}`;
  };

  const addDialogTitleContent = () => {
    return (
      <Box p={"15px"}>
        {isAddDialogOpen && (
          <Typography
            variant="h4"
            color={theme.palette.primary.main}
            textAlign={"center"}
          >
            Add Sender Domain
          </Typography>
        )}
        {isOpenStartVerification && (
          <Typography
            variant="h4"
            color={theme.palette.primary.main}
            textAlign={"center"}
          >
            We need below information to verify your domain.
          </Typography>
        )}
      </Box>
    );
  };

  const verifyStatusButtonHandler = () => {
    fetchDomainStatus();
  };

  const addDialogBodyContent = () => {
    return (
      <Box p={2}>
        <Box>
          <CustomInput
            id="domains_domain_name"
            label="Domain Name"
            name="domainName"
            required
            type="text"
            placeHolder="example.com"
            value={domainToBeAdded.domainName.value}
            error={domainToBeAdded.domainName.error}
            onChange={onChangeHandler}
            propsToInputElement={{ maxlength: strings.DOMAIN_NAME_LIMIT }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontSize: "0.75rem" }}>
                    {domainToBeAdded.domainName.value.length} /{" "}
                    {strings.DOMAIN_NAME_LIMIT}
                  </span>
                </InputAdornment>
              ),
            }}
          />
          {isTruthy(domainToBeAdded.domainName.error) && (
            <FormHelperText error sx={{ paddingLeft: "13px" }}>
              {domainToBeAdded.domainName.error}
            </FormHelperText>
          )}
          {!domainToBeAdded.domainName.error && (
            <FormHelperText>
              * Enter a domain which you want to use as "from" in your emails.
            </FormHelperText>
          )}
        </Box>
        <Box sx={classes.inputContainer}>
          <InputLabel
            required
            shrink
            sx={{
              color: theme.palette.neutral[900],
              fontSize: "1.1rem",
              fontWeight: 500,
            }}
          >
            Sender ID
          </InputLabel>
          <TextField
            id="domains_sender_id"
            name="senderId"
            variant="outlined"
            required
            type="text"
            placeholder={"Sender ID"}
            value={domainToBeAdded.senderId.value}
            error={domainToBeAdded.senderId.error}
            sx={classes.senderIdTextarea}
            onChange={onChangeHandler}
            inputProps={{ maxlength: 100 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" variant="filled">
                  <Typography>
                    {isTruthy(domainToBeAdded.domainName.value)
                      ? getDomainNameForAdornment(
                          domainToBeAdded.domainName.value
                        )
                      : "@example.com"}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          {isTruthy(domainToBeAdded.senderId.error) && (
            <FormHelperText error sx={{ paddingLeft: "13px" }}>
              {domainToBeAdded.senderId.error}
            </FormHelperText>
          )}
        </Box>
        <Box sx={classes.inputContainer}>
          <CustomInput
            id="domains_sender_name"
            label="Sender Name"
            name="senderName"
            required
            type="text"
            placeHolder="Sender Name"
            value={domainToBeAdded.senderName.value}
            error={domainToBeAdded.senderName.error}
            onChange={onChangeHandler}
            propsToInputElement={{ maxlength: strings.SENDER_NAME_LIMIT }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontSize: "0.75rem" }}>
                    {domainToBeAdded.senderName.value.length} /{" "}
                    {strings.SENDER_NAME_LIMIT}
                  </span>
                </InputAdornment>
              ),
            }}
          />
          {isTruthy(domainToBeAdded.senderName.error) && (
            <FormHelperText error sx={{ paddingLeft: "13px" }}>
              {domainToBeAdded.senderName.error}
            </FormHelperText>
          )}
        </Box>
      </Box>
    );
  };

  const addDialogFooterContent = () => {
    return (
      <Stack
        direction={{ lg: "row", md: "row", sm: "row", xs: "column" }}
        spacing={2}
        mb={1}
        alignItems="center"
      >
        <Button
          id="add_domain_cancel_button"
          variant="outlined"
          onClick={addDialogCloseHandler}
          disabled={isLoading}
          sx={{ width: "7.125rem", height: "2.5rem" }}
        >
          Cancel
        </Button>
        <Button
          id="add_domain_submit_button"
          variant="contained"
          size="small"
          onClick={newDomainAddHandler}
          disabled={isLoading}
        >
          Add Domain
        </Button>
      </Stack>
    );
  };

  const handleDeleteFileName = () => {
    setSampleEmailUrl(initialSampleEmailUrl);
  };

  const uploadFile = async (event: any) => {
    try {
      const fileData = event[0];
      setIsLoading(true);
      let fileNames = fileData?.name;
      const data = new FormData();
      data.append("file", fileData);
      const res = await uploadEmailSampleDoc(data, loggedInUserEmail);
      setSampleEmailUrl({
        ...sampleEmailUrl,
        fileName: fileNames,
        value: res?.path,
        error: "",
      });
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleCloseModel = () => {
    setIsOpenStartVerification(false);
    setWebsiteDescription(initialKeyValue);
    setSampleEmailUrl(initialSampleEmailUrl);
    setVerificationQNA(qnaMap);
  };

  const getDropzone = () => {
    return (
      <Grid>
        <InputLabel
          shrink
          sx={{
            color: theme.palette.neutral[900],
            fontSize: "1.1rem",
            fontWeight: 500,
          }}
        >
          Please upload the sample of email that you want to send
          <span
            style={{
              color: "red",
              fontSize: "15px",
              lineHeight: 1,
              verticalAlign: "super",
              marginLeft: "2px",
            }}
          >
            *
          </span>
        </InputLabel>
        <Box sx={classes.dropzoneAreaBase}>
          <CustomDropzone
            sx={{ height: "8rem" }}
            onAdd={uploadFile}
            maxFileSize={10 * 1024 * 1024}
            acceptedFiles={[".pdf", ".jpeg", ".png"]}
            showAlerts={false}
            multipleFileUpload={false}
            dropzoneText={"Choose a file or drag & drop it here"}
            icon={DropZoneIcon}
          />
          {!isTruthy(sampleEmailUrl.value) && (
            <FormHelperText error sx={{ paddingLeft: "10px" }}>
              {sampleEmailUrl.error}
            </FormHelperText>
          )}
        </Box>
        {sampleEmailUrl?.fileName && (
          <Chip
            sx={{ marginTop: 1 }}
            label={sampleEmailUrl?.fileName}
            variant="filled"
            onDelete={handleDeleteFileName}
          />
        )}
      </Grid>
    );
  };

  const onDescriptionHandler = (event: any) => {
    setWebsiteDescription({
      ...websiteDescription,
      value: event.target.value,
      error: "",
    });
  };

  const handleGroupTypeOnchange = (event: SelectChangeEvent) => {
    // handleFieldChange(event.target.name, event.target.value);
    setVerificationQNA({
      ...verificationQNA,
      [event.target.name]: {
        ...verificationQNA[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };
  const addVerificationBodyContent = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Box sx={{ marginTop: "5px" }}>
            <InputLabel
              shrink
              sx={{
                color: theme.palette.neutral[900],
                fontSize: "1.1rem",
                fontWeight: 500,
              }}
            >
              Description about your website{" "}
              <span
                style={{
                  color: "red",
                  fontSize: "15px",
                  lineHeight: 1,
                  verticalAlign: "super",
                  marginLeft: "2px",
                }}
              >
                *
              </span>
            </InputLabel>
            <CustomTextarea
              inputName={"description"}
              value={websiteDescription?.value}
              error={websiteDescription?.error}
              onChangeHandler={onDescriptionHandler}
              maxLength={strings.WEBSITE_DESCRIPTION_SIZE}
              textareaRows={2}
              allowTextFormatting={false}
            />
          </Box>
        </Grid>
        {Object.keys(verificationQNA).map((quetion: string) => {
          return (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              key={quetion}
              sx={{ marginTop: "10px" }}
            >
              {verificationQNA[quetion]?.component === "dropdown" ? (
                <Stack direction="column">
                  <InputLabel
                    id="simple-select"
                    shrink
                    sx={{
                      color: theme.palette.neutral[900],
                      fontSize: "1.1rem",
                      fontWeight: 500,
                    }}
                  >
                    {quetion + " "}{" "}
                    <span
                      style={{
                        color: "red",
                        fontSize: "15px",
                        lineHeight: 1,
                        verticalAlign: "super",
                        marginLeft: "2px",
                      }}
                    >
                      *
                    </span>
                  </InputLabel>
                  <Select
                    id="groupType"
                    value={verificationQNA[quetion]?.value}
                    name={quetion}
                    displayEmpty
                    renderValue={
                      verificationQNA[quetion]?.value !== ""
                        ? undefined
                        : () => verificationQNA[quetion]?.placeholder
                    }
                    onChange={handleGroupTypeOnchange}
                    error={verificationQNA[quetion]?.error}
                  >
                    {verificationQNA[quetion]?.options.map(
                      (menuOption: string) => (
                        <MenuItem value={menuOption} key={menuOption}>
                          {menuOption}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {!isTruthy(verificationQNA[quetion]?.value) && (
                    <FormHelperText error sx={{ paddingLeft: "10px" }}>
                      {verificationQNA[quetion]?.error}
                    </FormHelperText>
                  )}
                </Stack>
              ) : (
                <Stack direction="column">
                  <CustomInput
                    required
                    label={quetion}
                    id={`question_${quetion}`}
                    type={verificationQNA[quetion]?.type}
                    name={quetion}
                    value={verificationQNA[quetion]?.value}
                    placeHolder={verificationQNA[quetion].placeholder}
                    propsToInputElement={{ maxlength: 50 }}
                    InputProps={{
                      endAdornment: verificationQNA[quetion]?.type !==
                        "number" && (
                        <InputAdornment position="end">
                          <span style={{ fontSize: "0.75rem" }}>
                            {verificationQNA[quetion].value?.length} /{" "}
                            {strings.ANSWER_SIZE}
                          </span>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event: any) => {
                      setVerificationQNA({
                        ...verificationQNA,
                        [quetion]: {
                          ...verificationQNA[quetion],
                          value: event.target.value,
                          error: "",
                        },
                      });
                    }}
                    error={verificationQNA[quetion]?.error}
                  />
                  {isTruthy(verificationQNA[quetion]?.error) && (
                    <FormHelperText error sx={{ paddingLeft: "13px" }}>
                      {verificationQNA[quetion]?.error}
                    </FormHelperText>
                  )}
                </Stack>
              )}
            </Grid>
          );
        })}
        <Grid>{getDropzone()}</Grid>
      </Stack>
    );
  };

  const addVerificationFooterContent = () => {
    return (
      <Stack
        direction={{ lg: "row", md: "row", sm: "column", xs: "column" }}
        spacing={2}
        mb={1}
      >
        <Button
          id="cancel_button"
          variant="outlined"
          onClick={() => setIsOpenStartVerification(false)}
          sx={{ width: "7.125rem", height: "2.5rem" }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          id="submit_verification_button"
          variant="contained"
          size="small"
          onClick={() => {
            handleSubmitForm();
          }}
          disabled={isLoading}
          sx={{ width: "7.125rem", height: "2.5rem" }}
        >
          Submit
        </Button>
      </Stack>
    );
  };

  const getDomainsPageHeading = () => {
    return (
      <Stack
        direction={{ lg: "row", md: "row", sm: "row", xs: "row" }}
        justifyContent={{
          lg: "space-between",
          md: "space-between",
          sm: "space-between",
          xs: "space-between",
        }}
        alignItems={{
          lg: "center",
          md: "center",
          sm: "center",
          xs: "center",
        }}
        spacing={3}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {!isDomainDetailsPage() && (
            <Typography variant="h5" color="text.primary">
              Domains
            </Typography>
          )}
          {isDomainDetailsPage() && (
            <Stack
              direction={{ lg: "row", md: "row", sm: "row", xs: "column" }}
              spacing={2}
              alignItems={{
                lg: "center",
                md: "center",
                sm: "center",
                xs: "flex-start",
              }}
              sx={{ alignItems: "center" }}
            >
              <Stack direction="row" alignItems="center">
                <Link
                  color="text.primary"
                  href={viewpaths.domainsViewPath}
                  sx={{
                    alignItems: "center",
                    display: "inline-flex",
                  }}
                  onClick={() => {
                    setDomainToBeDisplayed("");
                    setCurrentPage(strings.domainsList);
                  }}
                  underline="hover"
                  component={RouterLink}
                >
                  <KeyboardBackspace
                    htmlColor="#475569"
                    sx={{ cursor: "pointer", pr: 1 }}
                  />
                  <Typography
                    variant="body1"
                    color="text.primary"
                    fontWeight={700}
                  >
                    {domainToBeDisplayed}
                  </Typography>
                </Link>
              </Stack>
              <Typography
                sx={{
                  color: getColors[domainStatus],
                  display: "flex",
                  alignItems: "center",
                }}
                variant="subtitle2"
              >
                <Box
                  sx={{
                    borderRadius: "50%",
                    height: 6,
                    width: 6,
                    mx: 0.5,
                    backgroundColor: getColors[domainStatus],
                  }}
                />
                {strings.EmailDomainStatusDisplayValue[domainStatus]}
              </Typography>
            </Stack>
          )}
        </Stack>
        {!isDomainDetailsPage() && getAddDomainButton()}
        {isDomainDetailsPage() && (
          <Stack
            direction={{ lg: "row", md: "row", sm: "row", xs: "column" }}
            spacing={2}
          >
            <Button
              id="domains_start_verification_button"
              variant="contained"
              size="small"
              onClick={() => setIsOpenStartVerification(true)}
              disabled={
                !(
                  isStartVerification &&
                  domainStatus === strings.EmailDomainStatus.CNAMEPending
                )
              }
            >
              Start verification
            </Button>
            <Button
              id="domains_status_verify_button"
              variant="contained"
              size="small"
              startIcon={
                <Stack>
                  <img src={verifyStatus} />
                </Stack>
              }
              onClick={verifyStatusButtonHandler}
              disabled={isLoading}
            >
              Verify Status
            </Button>
          </Stack>
        )}
      </Stack>
    );
  };

  const getDomainsContentPage = () => {
    switch (currentPage) {
      case strings.domainsList:
        return (
          <DomainList
            domains={domains}
            domainViewButtonHandler={domainViewButtonHandler}
            isLoading={isLoading}
          />
        );
      case strings.domainDetails:
        return (
          <DomainDetails
            domainStatus={domainStatus}
            domainToBeDisplayed={domainToBeDisplayed}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            domainViewButtonHandler={domainViewButtonHandler}
            isStartVerification={isStartVerification}
            setIsStartVerification={setIsStartVerification}
          />
        );
      default:
        return (
          <DomainList
            domains={domains}
            domainViewButtonHandler={domainViewButtonHandler}
            isLoading={isLoading}
          />
        );
    }
  };

  const getDomainsPage = () => {
    return (
      <>
        <Stack direction="column" spacing={2}>
          {getDomainsPageHeading()}
          {getDomainsContentPage()}
          <CustomDialog
            isDialogOpen={isAddDialogOpen}
            closable
            closeButtonVisibility={true}
            handleDialogClose={addDialogCloseHandler}
            title={addDialogTitleContent()}
            body={addDialogBodyContent()}
            footer={addDialogFooterContent()}
            maxWidth="sm"
          />
          <CustomDialog
            isDialogOpen={isOpenStartVerification}
            closable
            closeButtonVisibility={true}
            handleDialogClose={handleCloseModel}
            width={"700px"}
            title={addDialogTitleContent()}
            body={addVerificationBodyContent()}
            footer={addVerificationFooterContent()}
            maxWidth="sm"
          />
        </Stack>
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {getDomainsPage()}
    </CenteredAuthenticatedLayout>
  );
};

export default Domains;
